import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Star, RefreshCw, ChevronDown, ChevronRight, CheckCircle2, Loader2,
  DollarSign, Clock, Package,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sub-tab keys — Overview (with expandable orders) and Payout Log.
const SUB_TABS = [
  { key: "overview", label: "Overview" },
  { key: "payouts",  label: "Biweekly Payouts" },
];

export default function AffiliatesTab({ token }) {
  const [sub, setSub] = useState("overview");
  return (
    <div data-testid="admin-affiliates-panel" className="space-y-6">
      <div className="inline-flex p-1 bg-white border border-charcoal-100 rounded-full">
        {SUB_TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            data-testid={`admin-affiliates-tab-${t.key}`}
            onClick={() => setSub(t.key)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
              sub === t.key ? "bg-[#8B2252] text-white shadow-sm" : "text-charcoal-500 hover:text-charcoal-700"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {sub === "overview" ? <OverviewPanel token={token} /> : <PayoutsPanel token={token} />}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Overview — all affiliates + stats + expandable orders
// -----------------------------------------------------------------------------
const OverviewPanel = ({ token }) => {
  const [rows, setRows] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const { data } = await axios.get(`${API}/admin/affiliates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows(data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load affiliates");
      setRows([]);
    }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const toggle = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <div className="surface-card p-6 md:p-8">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="overline mb-1">Active affiliates</p>
          <h2 className="font-display text-2xl font-bold text-charcoal-700">
            All affiliate partners
          </h2>
        </div>
        <button type="button" onClick={load} className="btn-secondary text-sm" data-testid="affiliates-refresh">
          <RefreshCw className="w-4 h-4 mr-1.5" />
          Refresh
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-2xl bg-red-50 border border-red-100 text-red-700 text-sm">
          {error}
        </div>
      )}

      {rows === null ? (
        <div className="py-8 text-center text-charcoal-400 text-sm">Loading…</div>
      ) : rows.length === 0 ? (
        <div data-testid="affiliates-empty" className="py-10 text-center">
          <Star className="w-6 h-6 text-charcoal-300 mx-auto mb-2" strokeWidth={1.5} />
          <p className="text-sm text-charcoal-500">
            No active affiliates yet. Go to the Customers tab → Affiliate button to activate one.
          </p>
        </div>
      ) : (
        <div data-testid="affiliates-list" className="divide-y divide-charcoal-100">
          {rows.map((a) => (
            <AffiliateRow
              key={a.id} affiliate={a}
              token={token}
              expanded={!!expanded[a.id]}
              onToggle={() => toggle(a.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const AffiliateRow = ({ affiliate, token, expanded, onToggle }) => {
  const [orders, setOrders] = useState(null);

  useEffect(() => {
    if (!expanded || orders !== null) return;
    axios.get(`${API}/admin/affiliates/${affiliate.id}/orders`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(({ data }) => setOrders(data || []))
      .catch(() => setOrders([]));
  }, [expanded, orders, affiliate.id, token]);

  return (
    <div className="py-4" data-testid={`affiliate-row-${affiliate.id}`}>
      <button
        type="button"
        onClick={onToggle}
        data-testid={`affiliate-row-toggle-${affiliate.id}`}
        className="w-full flex flex-col md:flex-row md:items-center gap-3 md:gap-6 text-left"
      >
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-charcoal-700">
            {affiliate.first_name} {affiliate.last_name}
            {affiliate.referral_code && (
              <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-mono px-2 py-0.5 rounded-full bg-[#8B2252]/10 text-[#8B2252] uppercase">
                {affiliate.referral_code}
              </span>
            )}
          </div>
          <div className="text-xs text-charcoal-400">{affiliate.email}</div>
        </div>
        <div className="grid grid-cols-3 md:flex md:items-center gap-4 md:gap-6 text-xs">
          <Stat label="Sales" value={`$${Number(affiliate.total_sales || 0).toFixed(2)}`} />
          <Stat label="Unpaid" value={`$${Number(affiliate.unpaid_commission || 0).toFixed(2)}`} accent />
          <Stat label="Paid" value={`$${Number(affiliate.paid_commission || 0).toFixed(2)}`} />
        </div>
        {expanded
          ? <ChevronDown className="w-4 h-4 text-charcoal-400" />
          : <ChevronRight className="w-4 h-4 text-charcoal-400" />}
      </button>

      {expanded && (
        <div className="mt-4 ml-2 pl-4 border-l border-charcoal-100">
          <div className="text-xs font-semibold text-charcoal-400 uppercase tracking-wider mb-2">
            Referred orders ({affiliate.referral_count})
          </div>
          {orders === null ? (
            <div className="text-xs text-charcoal-400">Loading…</div>
          ) : orders.length === 0 ? (
            <div className="text-xs text-charcoal-400">No referred orders yet.</div>
          ) : (
            <ul className="space-y-1.5" data-testid={`affiliate-orders-list-${affiliate.id}`}>
              {orders.map((o) => (
                <li key={o.id} className="text-xs text-charcoal-600 flex flex-wrap gap-2 items-center">
                  <span className="font-mono text-[#8B2252]">{o.order_number}</span>
                  <span className="text-charcoal-400">
                    · {o.buyer_email} · ${Number(o.order_total || 0).toFixed(2)} × {o.commission_pct}%
                  </span>
                  <span className="font-semibold text-charcoal-700">= ${Number(o.commission_amount || 0).toFixed(2)}</span>
                  <span className={`ml-auto px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                    o.payout_status === "paid" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-800"
                  }`}>
                    {o.payout_status}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, value, accent }) => (
  <div>
    <div className="text-charcoal-400 uppercase tracking-wider text-[10px]">{label}</div>
    <div className={`font-semibold ${accent ? "text-[#8B2252]" : "text-charcoal-700"}`}>{value}</div>
  </div>
);

// -----------------------------------------------------------------------------
// Payouts — biweekly period buckets + mark-as-paid + history
// -----------------------------------------------------------------------------
const PayoutsPanel = ({ token }) => {
  const [affiliates, setAffiliates] = useState(null);
  const [selectedId, setSelectedId] = useState("");
  const [periods, setPeriods] = useState(null);
  const [history, setHistory] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/admin/affiliates`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(({ data }) => {
        setAffiliates(data || []);
        if ((data || []).length > 0 && !selectedId) setSelectedId(data[0].id);
      })
      .catch(() => setAffiliates([]));
  }, [token, selectedId]);

  const loadFor = useCallback(async (id) => {
    if (!id) return;
    setError("");
    try {
      const [p, h] = await Promise.all([
        axios.get(`${API}/admin/affiliates/${id}/periods`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/admin/affiliates/${id}/payouts`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setPeriods(p.data || []);
      setHistory(h.data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load payouts");
    }
  }, [token]);

  useEffect(() => { if (selectedId) loadFor(selectedId); }, [selectedId, loadFor]);

  const markPaid = async (period) => {
    const note = window.prompt(
      `Mark ${period.order_count} order(s) as paid ($${period.total_commission})? ` +
      "Add an optional note (e.g. 'Sent via Zelle 7/15'):",
      "",
    );
    if (note === null) return;
    try {
      const { data } = await axios.post(
        `${API}/admin/affiliates/${selectedId}/payouts`,
        {
          period_start: period.period_start,
          period_end:   period.period_end,
          note:         note.trim(),
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Marked $${data.total_commission} paid across ${data.order_count} orders.`);
      loadFor(selectedId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not mark paid");
    }
  };

  if (affiliates === null) return <div className="surface-card p-8 text-sm text-charcoal-400">Loading…</div>;
  if (affiliates.length === 0) return (
    <div className="surface-card p-10 text-center">
      <p className="text-sm text-charcoal-500">No active affiliates yet.</p>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="surface-card p-4 flex flex-wrap items-center gap-3">
        <label className="text-xs uppercase tracking-[0.18em] text-charcoal-400 font-semibold">
          Affiliate
        </label>
        <select
          data-testid="payouts-affiliate-select"
          value={selectedId}
          onChange={(e) => { setSelectedId(e.target.value); setPeriods(null); setHistory(null); }}
          className="input flex-1 max-w-md"
        >
          {affiliates.map((a) => (
            <option key={a.id} value={a.id}>
              {a.first_name} {a.last_name} ({a.email})
            </option>
          ))}
        </select>
      </div>

      {error && (
        <div className="p-3 rounded-2xl bg-red-50 border border-red-100 text-red-700 text-sm">{error}</div>
      )}

      {/* Unpaid periods */}
      <div className="surface-card p-6 md:p-8">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="w-4 h-4 text-amber-700" strokeWidth={2} />
          <p className="overline">Unpaid periods (biweekly)</p>
        </div>
        {periods === null ? (
          <div className="text-xs text-charcoal-400">Loading…</div>
        ) : periods.length === 0 ? (
          <div data-testid="payouts-no-unpaid" className="text-sm text-charcoal-500 py-6 text-center">
            All caught up — no unpaid referral orders.
          </div>
        ) : (
          <div className="divide-y divide-charcoal-100" data-testid="payouts-periods-list">
            {periods.map((p) => (
              <div
                key={`${p.period_start}-${p.period_end}`}
                data-testid={`payout-period-${p.period_start}`}
                className="py-3 flex flex-col md:flex-row md:items-center gap-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-charcoal-700">
                    {formatDate(p.period_start)} → {formatDate(p.period_end)}
                    {p.in_progress && (
                      <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 font-semibold uppercase">
                        In progress
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-charcoal-400">
                    <Package className="w-3 h-3 inline mr-1" />
                    {p.order_count} orders · ${Number(p.total_sales).toFixed(2)} in sales
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-display text-lg font-bold text-[#8B2252]">
                    ${Number(p.total_commission).toFixed(2)}
                  </div>
                  <button
                    type="button"
                    disabled={p.in_progress}
                    onClick={() => markPaid(p)}
                    data-testid={`payout-mark-paid-${p.period_start}`}
                    className={`text-xs px-3 py-1.5 rounded-full ${
                      p.in_progress
                        ? "bg-charcoal-100 text-charcoal-300 cursor-not-allowed"
                        : "bg-[#8B2252] text-white hover:bg-[#7A1F49]"
                    }`}
                  >
                    {p.in_progress ? "Waiting for period to close" : "Mark as paid"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Payout history */}
      <div className="surface-card p-6 md:p-8">
        <div className="flex items-center gap-2 mb-4">
          <CheckCircle2 className="w-4 h-4 text-emerald-700" strokeWidth={2} />
          <p className="overline">Payout history</p>
        </div>
        {history === null ? (
          <div className="text-xs text-charcoal-400">Loading…</div>
        ) : history.length === 0 ? (
          <div data-testid="payouts-history-empty" className="text-sm text-charcoal-400 py-6 text-center">
            No payouts recorded yet.
          </div>
        ) : (
          <div className="divide-y divide-charcoal-100" data-testid="payouts-history-list">
            {history.map((h) => (
              <div key={h.id} className="py-3 flex flex-col md:flex-row md:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-charcoal-700">
                    {formatDate(h.period_start)} → {formatDate(h.period_end)}
                  </div>
                  <div className="text-xs text-charcoal-400">
                    Paid {h.paid_at?.slice(0, 10)} by {h.paid_by_admin_email}
                    {h.note && <> · <em>{h.note}</em></>}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-display text-lg font-bold text-emerald-700">
                    ${Number(h.total_commission).toFixed(2)}
                  </div>
                  <div className="text-[10px] text-charcoal-400">
                    {h.order_count} orders · ${Number(h.total_sales).toFixed(2)} in sales
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso + "T00:00:00Z").toLocaleDateString(undefined, {
      month: "short", day: "numeric", year: "numeric",
    });
  } catch { return iso; }
};
